/**
 * tagCounts — Track 10 / EFISSUE-820 Part 2, reworked by
 * track-tagcounts-counter (spec #60414).
 *
 * Tag usage counts shown beside tags in the composer's tag picker.
 *
 * Counts are exact and come from Firestore count aggregation queries
 * (`getCountFromServer`, one per tag the picker shows) on
 * `team_log_messages` — never from reading the documents. Nothing is
 * fetched at app start; the picker asks for its tags when it first opens.
 *
 * Results are cached per tag for 10 minutes, in memory and in sessionStorage
 * (keyed by workspace) so a page reload inside the window reads nothing.
 * Every sessionStorage access is wrapped in try/catch: when storage is
 * unavailable the in-memory cache still works.
 */

import { collection, getCountFromServer, query, where } from "firebase/firestore";
import { db } from "./firebase";

export const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const COLLECTION = "team_log_messages";
const STORAGE_PREFIX = "relay:tagCounts:v1:";

interface Entry {
  n: number;
  at: number;
}
type WorkspaceEntries = Record<string, Entry>;

const memory = new Map<string, WorkspaceEntries>();
let currentWorkspaceId: string | null = null;
const inflight = new Map<string, Promise<number | null>>();

function readStorage(workspaceId: string): WorkspaceEntries {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + workspaceId);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: WorkspaceEntries = {};
    for (const [tag, v] of Object.entries(parsed as Record<string, unknown>)) {
      const e = v as Partial<Entry> | null;
      if (e && typeof e.n === "number" && typeof e.at === "number") out[tag] = { n: e.n, at: e.at };
    }
    return out;
  } catch {
    return {};
  }
}

function writeStorage(workspaceId: string, entries: WorkspaceEntries): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + workspaceId, JSON.stringify(entries));
  } catch {
    // Storage unavailable or full: the in-memory cache still serves this session.
  }
}

function entriesFor(workspaceId: string): WorkspaceEntries {
  let entries = memory.get(workspaceId);
  if (!entries) {
    entries = readStorage(workspaceId);
    memory.set(workspaceId, entries);
  }
  return entries;
}

function isFresh(e: Entry | undefined, now: number): e is Entry {
  return !!e && now - e.at < CACHE_TTL_MS;
}

async function countTag(workspaceId: string, tag: string): Promise<number | null> {
  const key = `${workspaceId}\u0000${tag}`;
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = (async () => {
    try {
      const q = query(
        collection(db, COLLECTION),
        where("workspace_id", "==", workspaceId),
        where("tags", "array-contains", tag),
      );
      const snap = await getCountFromServer(q);
      return snap.data().count;
    } catch (err) {
      console.warn("[tagCounts] Count failed for", tag, err);
      return null;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

/**
 * Make sure counts for `tags` are cached (fresh within 10 minutes), fetching
 * only the missing or stale ones with one count query each. Returns true when
 * anything was fetched. Call when the tag UI opens, not at startup.
 */
export async function loadTagCounts(workspaceId: string, tags: readonly string[]): Promise<boolean> {
  currentWorkspaceId = workspaceId;
  const entries = entriesFor(workspaceId);
  const now = Date.now();
  const wanted = [...new Set(tags)].filter((t) => !isFresh(entries[t], now));
  if (wanted.length === 0) return false;

  const results = await Promise.all(wanted.map((t) => countTag(workspaceId, t)));
  const at = Date.now();
  let changed = false;
  wanted.forEach((t, i) => {
    const n = results[i];
    if (n !== null) {
      entries[t] = { n, at };
      changed = true;
    }
  });
  if (changed) writeStorage(workspaceId, entries);
  return changed;
}

/**
 * Synchronous tag count lookup for the workspace last passed to
 * `loadTagCounts`. Returns null if that tag's count isn't loaded yet.
 */
export function getTagCountSync(tag: string): number | null {
  if (!currentWorkspaceId) return null;
  return memory.get(currentWorkspaceId)?.[tag]?.n ?? null;
}

/** Test hook: drop the in-memory cache (sessionStorage is left alone). */
export function resetTagCountsMemoryForTests(): void {
  memory.clear();
  inflight.clear();
  currentWorkspaceId = null;
}
